import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getStationaryList,
  addStationaryToBusiness,
  updateStationary,
  deleteStationary,
} from "../../../../../../apis/Business/OtherVehicleApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { getFuelVehicleDropdown } from "../../../../../../apis/Dropdown";

const FossilFuelsTable = ({
  businessId,
  subcategory,
  subcategory_description,
  subcategoryId,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownFuel, setDropdownFuel] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getFuelVehicleDropdown({ setDropdownData: setDropdownFuel });
    getStationaryList({
      businessId: businessId,
      setObjects: setData,
      subcategory: subcategory,
      subcategory_description: subcategory_description,
      subcategoryId: subcategoryId,
    });
  }, [businessId, subcategory, subcategoryId, subcategory_description]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownFuel?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["fuel_type.value"]?.value || row.fuel_type?.value;

        if (materialValue) {
          const selectedMaterial = dropdownFuel.find(
            (option) =>
              option?.dropdown_name === materialValue ||
              option?.dropdown_name_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownFuel]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "fuel_type.value": {
        targetField: "fuel_amount",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) =>
              option?.dropdown_name === selectedValue ||
              option?.dropdown_name_en === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "fuel_type.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateStationary({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        fuel_type: editedItem["fuel_type.value"]
          ? editedItem["fuel_type.value"].value
          : editedItem["fuel_type"]
          ? editedItem["fuel_type"].value
          : null,
        subcategoryId: subcategoryId,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [
      businessId,
      data,
      setData,
      subcategory,
      subcategory_description,
      subcategoryId,
    ]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addStationaryToBusiness({
        businessId,
        ...newItem,
        fuel_type: newItem["fuel_type.value"]
          ? newItem["fuel_type.value"].value
          : null,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        subcategoryId: subcategoryId,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory, subcategory_description, subcategoryId]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteStationary({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "fuel_type.value",
        header: `${t("FUEL_TYPE")}*`,
        type: "dropdown",
        dropdownData: dropdownFuel,
        disableSortBy: true,
        width: "110px",
      },
      {
        accessor: "fuel_amount",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("QUANTITY")}* (${unitMaterials[rowId] || "xx"})`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: (rowId) => `${unitMaterials[rowId] || "xx"}`,
      },
      {
        accessor: "biomass_fuel_proportion",
        header: t("PROPORTION_FUEL"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: "%",
        optional: true,
      },
      {
        accessor: "fuel_comply",
        header: t("FUEL_COMPLY"),
        type: "switch",
        disableSortBy: true,
        width: "90px",
      },
      {
        accessor: "manuel",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `Manual gCO₂ /${unitMaterials[rowId] || "xx"}`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("OTHER_VEHICLE_MANUEL_TIP"),
        optional: true,
        unit: (rowId) => `Manual gCO₂/${unitMaterials[rowId] || "xx"}`,
      },
      {
        accessor: "leased_fuel_proportion",
        header: t("LEASED_PROPORTION"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("LEASED_PROPORTION_TIP"),
        unit: "%",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: `${t("CARBON_OUTPUT")} (Scope 1)`,
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
      {
        accessor: "carbon_scope_3",
        header: `${t("CARBON_OUTPUT")} (S:3, C:8)`,
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value || 0} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownFuel, t, unitMaterials]
  );

  const disableRules = {
    carbon: () => true,
    carbon_scope_3: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: `${t("TOTAL_CARBON")} (Scope 1)` },
    carbon_scope_3: { label: `${t("TOTAL_CARBON")} (S:3, C:8)` },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "fuel_type.value": dropdownFuel,
    }),
    [dropdownFuel]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{
        carbon: CustomInputComponent,
        carbon_scope_3: CustomInputComponent,
      }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default FossilFuelsTable;
